import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { reclassifyMessage } from '../../services/memoryCategoriesService';
import CategoryBadge from '../memory/CategoryBadge';

/**
 * ReclassifyMessage Component
 * 
 * Modal dialog for admins to reclassify messages.
 */
const ReclassifyMessage = ({ message, open, onOpenChange, onSuccess }) => {
  const [newDomain, setNewDomain] = useState('');
  const [newSeverity, setNewSeverity] = useState('none');
  const [selectedTags, setSelectedTags] = useState([]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const domains = [
    { value: 'communications', label: 'Communications' },
    { value: 'gross_motor', label: 'Gross Motor' },
    { value: 'fine_motor', label: 'Fine Motor' },
    { value: 'cognitive', label: 'Cognitive' },
    { value: 'personal_social', label: 'Personal/Social' },
    { value: 'general', label: 'General' }
  ];

  const severities = [
    { value: 'none', label: 'None' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' }
  ];

  const availableTags = [
    { value: 'urgent', label: 'Urgent' },
    { value: 'concern', label: 'Concern' },
    { value: 'achievement', label: 'Achievement' },
    { value: 'question', label: 'Question' },
    { value: 'follow_up', label: 'Follow-up' },
    { value: 'milestone_matched', label: 'Milestone Matched' }
  ];

  useEffect(() => {
    if (message && open) {
      const categories = message.categories || {};
      setNewDomain(categories.primary || '');
      setNewSeverity(categories.severity || 'none');
      setSelectedTags(categories.secondary_tags || []);
      setReason('');
    }
  }, [message, open]);

  const handleTagToggle = (tagValue) => {
    setSelectedTags(prev =>
      prev.includes(tagValue)
        ? prev.filter(t => t !== tagValue)
        : [...prev, tagValue]
    );
  };

  const handleSubmit = async () => {
    if (!newDomain) {
      toast.error('Please select a domain');
      return;
    }

    if (!reason.trim()) {
      toast.error('Please provide a reason for reclassification');
      return;
    }

    setSubmitting(true);
    try {
      await reclassifyMessage(message.id, {
        new_domain: newDomain,
        new_tags: selectedTags,
        severity: newSeverity,
        reason: reason.trim()
      });
      toast.success('Message reclassified successfully');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to reclassify:', error);
      toast.error('Failed to reclassify message');
    } finally {
      setSubmitting(false);
    }
  };

  if (!message) return null;

  const currentCategories = message.categories || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reclassify Message</DialogTitle>
          <DialogDescription>
            Update the classification for this message. This action will be logged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Classification */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Current Classification</Label>
            <div className="p-3 bg-gray-50 rounded border">
              <div className="flex items-center gap-2 mb-2">
                <CategoryBadge 
                  domain={currentCategories.primary} 
                  severity={currentCategories.severity}
                />
              </div>
              {currentCategories.secondary_tags && currentCategories.secondary_tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {currentCategories.secondary_tags.map((tag, idx) => (
                    <span key={idx} className="text-xs px-2 py-1 bg-gray-200 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-sm mt-2 text-gray-600">{message.content}</p>
            </div>
          </div>

          {/* New Domain */}
          <div>
            <Label htmlFor="domain" className="text-sm font-medium mb-2 block">
              New Domain *
            </Label>
            <Select value={newDomain} onValueChange={setNewDomain}>
              <SelectTrigger id="domain">
                <SelectValue placeholder="Select domain" />
              </SelectTrigger>
              <SelectContent>
                {domains.map((domain) => (
                  <SelectItem key={domain.value} value={domain.value}>
                    {domain.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* New Severity */}
          <div>
            <Label htmlFor="severity" className="text-sm font-medium mb-2 block">
              New Severity
            </Label>
            <Select value={newSeverity} onValueChange={setNewSeverity}>
              <SelectTrigger id="severity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {severities.map((severity) => (
                  <SelectItem key={severity.value} value={severity.value}>
                    {severity.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Secondary Tags */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Secondary Tags</Label>
            <div className="grid grid-cols-2 gap-2">
              {availableTags.map((tag) => (
                <div key={tag.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`tag-${tag.value}`}
                    checked={selectedTags.includes(tag.value)}
                    onCheckedChange={() => handleTagToggle(tag.value)}
                  />
                  <label
                    htmlFor={`tag-${tag.value}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {tag.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Reason */}
          <div>
            <Label htmlFor="reason" className="text-sm font-medium mb-2 block">
              Reason for Reclassification *
            </Label>
            <Textarea
              id="reason"
              placeholder="Explain why this message needs to be reclassified..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !newDomain || !reason.trim()}>
            {submitting ? 'Reclassifying...' : 'Reclassify'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReclassifyMessage;

